import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Textarea,
} from "@lendcord/concord";
import { useDeleteApplicationMutation, useRejectApplicationMutation } from "@/features/onboarding/api/onboarding.api";
import { isQueryApiError } from "@/app/store/baseApi";

interface ApplicationActionsProps {
  applicationId: string;
  isEditAllowed: boolean;
}

type ConfirmAction = "reject" | "delete" | null;

// Reject and delete are both soft, terminal actions (status -> REJECTED /
// DELETED, no data removed — see lendcord-core's docs/flows/
// onboarding-review-flow.md, section 7) gated by the same ApplicationEditPolicy
// rule as every other mutating endpoint here. Once either succeeds,
// invalidating the application's tag flips its status and
// ApplicationReviewFlow's own terminal-status check routes to the read-only
// detail view automatically — no local navigation needed here. Both require a
// Reason, which becomes the application's `remarks` server-side (overwriting
// whatever the onboarding agent originally entered) — the backend itself
// treats it as optional (see ApplicationRemarksRequest), this form is the one
// that requires it.
export function ApplicationActions({ applicationId, isEditAllowed }: ApplicationActionsProps) {
  const intl = useIntl();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [rejectApplication, { isLoading: isRejecting }] = useRejectApplicationMutation();
  const [deleteApplication, { isLoading: isDeleting }] = useDeleteApplicationMutation();

  const isSubmitting = isRejecting || isDeleting;

  // Same role/status rule ApplicationEditPolicy enforces server-side — hide
  // rather than disable, since a caller who can't edit (wrong role, or the
  // application already moved to a terminal status underneath them) has no
  // legitimate use for these at all.
  if (!isEditAllowed) return null;

  function openConfirm(action: Exclude<ConfirmAction, null>) {
    setError(undefined);
    setReason("");
    setReasonError(undefined);
    setConfirmAction(action);
  }

  function handleClose() {
    if (isSubmitting) return;
    setConfirmAction(null);
  }

  async function handleConfirm() {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setReasonError(intl.formatMessage({ id: "lender.onboarding.review.reasonRequiredError" }));
      return;
    }
    setReasonError(undefined);

    try {
      if (confirmAction === "reject") {
        await rejectApplication({ id: applicationId, remarks: trimmedReason }).unwrap();
      } else if (confirmAction === "delete") {
        await deleteApplication({ id: applicationId, remarks: trimmedReason }).unwrap();
      }
      setConfirmAction(null);
    } catch (submitError) {
      setError(
        isQueryApiError(submitError)
          ? submitError.message
          : intl.formatMessage({ id: "lender.onboarding.review.actionGenericError" }),
      );
    }
  }

  return (
    <>
      <div className="flex gap-3">
        <Button type="button" variant="secondary" onClick={() => openConfirm("delete")}>
          <FormattedMessage id="lender.onboarding.review.deleteButton" />
        </Button>
        <Button type="button" variant="destructive" onClick={() => openConfirm("reject")}>
          <FormattedMessage id="lender.onboarding.review.rejectButton" />
        </Button>
      </div>

      <Dialog open={confirmAction !== null} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                id={
                  confirmAction === "delete"
                    ? "lender.onboarding.review.deleteConfirmHeading"
                    : "lender.onboarding.review.rejectConfirmHeading"
                }
              />
            </DialogTitle>
            <DialogDescription>
              <FormattedMessage
                id={
                  confirmAction === "delete"
                    ? "lender.onboarding.review.deleteConfirmDescription"
                    : "lender.onboarding.review.rejectConfirmDescription"
                }
              />
            </DialogDescription>
          </DialogHeader>

          {error && <AlertBanner status="error">{error}</AlertBanner>}

          <FormField
            label={intl.formatMessage({ id: "lender.onboarding.review.reasonLabel" })}
            error={reasonError}
            required
          >
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={intl.formatMessage({
                id:
                  confirmAction === "delete"
                    ? "lender.onboarding.review.reasonPlaceholderDelete"
                    : "lender.onboarding.review.reasonPlaceholderReject",
              })}
              disabled={isSubmitting}
              rows={3}
            />
          </FormField>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              <FormattedMessage id="lender.onboarding.review.actionCancelButton" />
            </Button>
            <Button type="button" variant="destructive" onClick={handleConfirm} disabled={isSubmitting}>
              {isSubmitting ? (
                <FormattedMessage id="lender.onboarding.review.actionConfirmingButton" />
              ) : (
                <FormattedMessage id="lender.onboarding.review.actionConfirmButton" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
